/* eslint‑disable @typescript-eslint/no-use-before-define */
import { forceSimulation, forceCollide, forceManyBody } from "d3-force";
import { rand, randBool } from "@rosette/ts/src/util/random.js";

/* ----------  graph primitives  ---------- */

export interface VINode {
    id: number;
    x: number;
    y: number;
    radius: number;
    in: VIEdge[];
    out: VIEdge[];
    value: number;
    trueValue: number;
    updates: number;
    totalUpdates: number;
}

export interface VIEdge {
    id: number;
    from: VINode;
    to: VINode;
}

/* ----------  animation structures  ---------- */

export interface ValuePacket {
    fromId: number;
    toId: number;
    valueDelta: number; // signed!
}

export interface AnimNodeState {
    value: number;
    updates: number;
}

export interface AnimationState {
    nodeStates: Record<number, AnimNodeState>;
    packets: ValuePacket[];
}

/* ----------  simulator  ---------- */

export class ValueIterationSim {
    readonly nodes: VINode[];
    readonly edges: VIEdge[];
    readonly start: VINode;
    readonly win: VINode;
    readonly lose: VINode;
    readonly width: number;
    readonly height: number;
    private nextEdgeId = 0;

    constructor(
        numNodes: number,
        width: number,
        height: number,
        radius: number,
        importantRadius: number,
        border: number,
    ) {
        if (numNodes < 3)
            throw new Error("Need at least 3 nodes");

        this.width = width;
        this.height = height;

        /* ----------  construct nodes  ---------- */
        const nodes: VINode[] = [];
        const makeNode = (x: number, y: number, v: number, nodeRadius?: number): VINode => {
            const n: VINode = {
                id: nodes.length,
                x, y,
                radius: nodeRadius ?? radius,
                value: v,
                trueValue: v,
                in: [], out: [],
                updates: 0,
                totalUpdates: 0,
            };
            nodes.push(n);
            return n;
        };

        const nodeBorder = border + importantRadius;
        this.start = makeNode(nodeBorder, height / 2, NaN, importantRadius);
        this.win = makeNode(width - nodeBorder, nodeBorder, 100, importantRadius);
        this.lose = makeNode(width - nodeBorder, height - nodeBorder, 0, importantRadius);

        const left = nodeBorder + 2 * radius + importantRadius;
        const right = width - left;
        const top = nodeBorder;
        const bottom = height - top;

        const freeNodes: VINode[] = [];
        while (nodes.length < numNodes) {
            const [nx, ny] = generateNodePosition(width / 2, height / 2, radius, nodes.length - 3);
            freeNodes.push(makeNode(nx, ny, NaN));
        }
        this.nodes = nodes;

        /* ----------  position relaxation  ---------- */
        const sim = forceSimulation<VINode>(freeNodes)
            .force("charge", forceManyBody<VINode>().strength(-5))
            .force("collide", forceCollide<VINode>(2 * radius))
            .stop();

        for (let i = 0; i < 20; ++i) {
            sim.tick();
            for (const node of freeNodes) {
                node.x = Math.max(left, Math.min(right, node.x));
                node.y = Math.max(top, Math.min(bottom, node.y));
            }
        }

        /* ----------  generate edges  ---------- */
        this.edges = [];
        const nodePairs: [VINode, VINode, number][] = [];
        for (let i = 0; i < nodes.length; ++i) {
            for (let j = i + 1; j < nodes.length; ++j) {
                const a = nodes[i];
                const b = nodes[j];
                nodePairs.push([a, b, dist(a, b)]);
            }
        }
        nodePairs.sort((a, b) => a[2] - b[2]); // short first

        // connect until each node has at least 3 undirected edges
        for (const [a, b] of nodePairs) {
            if (intersectsAnyNodes(a, b, this.nodes, 2 * radius))
                continue;
            if (intersectsAnyEdges(a, b, this.edges))
                continue;
            this.addEdge(a, b); // undirected for now
        }

        /* ----------  orient edges  ---------- */
        // Make the shortest paths from win/lose to start directed.
        this.makeDirectedPath(this.shortestPath(this.win, this.start), this.win);
        this.makeDirectedPath(this.shortestPath(this.lose, this.start), this.lose);

        // Randomly direct remaining edges, and remove invalid edges.
        for (let index = 0; index < this.edges.length; ++index) {
            const edge = this.edges[index];

            let from = edge.from;
            let to = edge.to;

            // We don't want the win/lose/start states to be connected directly.
            const fromIsImportant = (from === this.start || from === this.win || from === this.lose);
            const toIsImportant = (to === this.start || to === this.win || to === this.lose);
            if (fromIsImportant && toIsImportant) {
                this.edges.splice(index, 1);
                index -= 1;
                continue;
            }

            // Edge is already directed. Leave it be.
            if (isEdgeDirected(edge))
                continue;

            if (randBool()) {
                // Swap to and from.
                const temp = to;
                to = from;
                from = temp;
            }

            // Make the edge directed.
            this.makeDirected(edge, from, to);
        }

        // Find any nodes that can't reach the start node, and fix that.
        for (const node of this.nodes) {
            if (node === this.start || node === this.win || node === this.lose)
                continue;
            if (reachable(node, this.start))
                continue;

            const path = this.shortestPath(node, this.start, true);
            this.makeDirectedPathUntilReachable(path, node, this.start);
        }

        // Make sure edges to the start node are not directed away from it,
        // and that edges to the win or loss nodes are not directed to them.
        for (const edge of this.edges) {
            if (edge.from === this.start || edge.to === this.win || edge.to === this.lose) {
                this.makeDirected(edge, edge.to, edge.from);
            }
        }

        // Remove nodes with no predecessors.
        let removedAny;
        do {
            removedAny = false;
            for (let index = 0; index < this.nodes.length; ++index) {
                const node = this.nodes[index];
                if (node === this.start || node === this.win || node === this.lose)
                    continue;

                const predecessors = node.in.map(e => e.from);
                if (predecessors.length <= 0) {
                    this.removeNode(index);
                    index -= 1;
                    removedAny = true;
                }
            }
        } while (removedAny);

        /* ----------  calculate true values  ---------- */
        this.runValueIteration();

        /* ----------  build animation frames  ---------- */
        this.animation = this.buildAnimation();
    }

    /* ----------  public animation  ---------- */

    readonly animation: AnimationState[];

    /* ----------  value iteration  ---------- */

    private runValueIteration(): void {
        let maxDiff;
        do {
            maxDiff = 0;
            for (const n of this.nodes) {
                if (n === this.win || n === this.lose)
                    continue;

                const predecessorValues = n.in.map((e) => {
                    const value = e.from.trueValue;
                    return isNaN(value) ? 50.0 : value;
                });
                if (predecessorValues.length <= 0)
                    continue;

                const newVal = mean(predecessorValues);
                maxDiff = (isNaN(n.trueValue) ? 100.0 : Math.max(maxDiff, Math.abs(n.trueValue - newVal)));
                n.trueValue = newVal;
            }
        } while (maxDiff > 0.1);
    }

    /* ----------  animation generation  ---------- */

    private recordNodeStates(): Record<number, AnimNodeState> {
        const states: Record<number, AnimNodeState> = {};
        for (const node of this.nodes) {
            states[node.id] = {
                value: node.value,
                updates: node.updates,
            };
        }
        return states;
    }

    private buildAnimation(): AnimationState[] {
        const visited = new Set<VINode>([this.win, this.lose]);
        const updating = new Set<VINode>();

        const states: AnimationState[] = [];

        // Initial state.
        states.push({
            nodeStates: this.recordNodeStates(),
            packets: [],
        });

        /* iteratively flood‑fill from visited */
        let iters = 0;
        while (iters < 1000) {
            iters += 1;

            if (updating.size <= 0) {
                // Add new nodes by following directed edges.
                for (const v of visited) {
                    for (const e of v.out) {
                        const n = e.to;
                        if (!visited.has(n)) {
                            updating.add(n);
                        }
                    }
                }
                // Add states that those states depend upon.
                let added = true;
                while (added) {
                    added = false;
                    for (const v of Array.from(updating)) {
                        for (const e of v.in) {
                            const n = e.from;
                            if (!visited.has(n) && !updating.has(n)) {
                                updating.add(n);
                                added = true;
                            }
                        }
                    }
                }
                // Nothing new? Finished.
                if (updating.size <= 0)
                    break;
            }

            // Save value of nodes.
            const nodeStates = this.recordNodeStates();

            // Update value of nodes.
            const packets: ValuePacket[] = [];
            for (const node of Array.from(updating)) {
                const predecessors = node.in.map(e => e.from);
                if (!predecessors.length)
                    throw new Error("No predecessors for node " + node.id);

                const predecessorValues = predecessors.map((node) => {
                    const value = nodeStates[node.id].value;
                    return isNaN(value) ? 50.0 : value;
                });

                const prevValue = node.value;
                const newValue = mean(predecessorValues);

                if (isNaN(prevValue) || Math.round(newValue) !== Math.round(prevValue)) {
                    // Create little packets of value to animate.
                    let updated = false;
                    for (const p of predecessors) {
                        const predecessorValue = nodeStates[p.id].value;
                        if (isNaN(predecessorValue) || Math.abs(predecessorValue - p.trueValue) >= 10)
                            continue;

                        const prevValueNotNaN = (isNaN(prevValue) ? 50.0 : prevValue);
                        const delta = (predecessorValue - prevValueNotNaN) / predecessors.length;
                        if (!isNaN(prevValue) && Math.abs(delta) < 1)
                            continue;

                        updated = true;
                        packets.push({
                            fromId: p.id,
                            toId: node.id,
                            valueDelta: delta,
                        });
                    }
                    if (updated) {
                        node.updates += 1;
                    }
                }

                if (node.updates > 0) {
                    node.value = (node.updates < 4 ? newValue : node.trueValue);
                }
                if (isNaN(node.value))
                    continue;

                // Node is finished updating.
                const diff = node.trueValue - node.value;
                if (node.updates > 0 && Math.abs(diff) <= Math.max(1.0, node.updates / 10.0)) {
                    node.value = node.trueValue;
                    updating.delete(node);
                    visited.add(node);
                }
            }

            if (packets.length > 0) {
                states.push({ nodeStates, packets });
            } else {
                throw new Error("Graph contains independent loops");
            }
        }
        if (iters >= 1000)
            throw new Error("Animation took over 1000 frames!");

        // Record totalUpdates.
        for (const node of this.nodes) {
            node.totalUpdates = node.updates;
        }

        // Final state.
        states.push({
            nodeStates: this.recordNodeStates(),
            packets: [],
        });
        return states;
    }

    /* ----------  helpers  ---------- */

    private addEdge(a: VINode, b: VINode): void {
        const e: VIEdge = { id: this.nextEdgeId++, from: a, to: b };
        // Bidirectional to begin with.
        a.out.push(e);
        b.out.push(e);
        a.in.push(e);
        b.in.push(e);
        this.edges.push(e);
    }

    private removeNode(nodeIndex: number): void {
        const node = this.nodes[nodeIndex];
        this.nodes.splice(nodeIndex, 1);
        for (let index = 0; index < this.edges.length; ++index) {
            const edge = this.edges[index];
            if (edge.from === node || edge.to === node) {
                this.removeEdge(index);
                index -= 1;
            }
        }
    }

    private removeEdge(edgeIndex: number) {
        const edge = this.edges[edgeIndex];
        this.edges.splice(edgeIndex, 1);
        for (const node of this.nodes) {
            removeFromEdgeSet(node.in, edge);
            removeFromEdgeSet(node.out, edge);
        }
    }

    private makeDirected(edge: VIEdge, from: VINode, to: VINode): void {
        if (edge.from !== from && edge.from !== to)
            throw new Error("Supplied \"from\" node is not in the edge");
        if (edge.to !== from && edge.to !== to)
            throw new Error("Supplied \"to\" node is not in the edge");

        edge.from = from;
        edge.to = to;

        addToEdgeSet(from.out, edge);
        removeFromEdgeSet(from.in, edge);

        removeFromEdgeSet(to.out, edge);
        addToEdgeSet(to.in, edge);
    }

    private makeDirectedPath(path: VIEdge[], start: VINode): void {
        let current = start;
        for (const e of path) {
            let from: VINode;
            let to: VINode;
            if (e.from === current) {
                from = e.from;
                to = e.to;
            } else {
                from = e.to;
                to = e.from;
            }
            this.makeDirected(e, from, to);
            current = to;
        }
    }

    private makeDirectedPathUntilReachable(path: VIEdge[], start: VINode, dest: VINode): void {
        let current = start;
        for (const e of path) {
            let from: VINode;
            let to: VINode;
            if (e.from === current) {
                from = e.from;
                to = e.to;
            } else {
                from = e.to;
                to = e.from;
            }
            this.makeDirected(e, from, to);
            current = to;
            if (to === dest)
                break;
            if (reachable(to, dest))
                break;
        }
    }

    private findEdges(n: VINode): VIEdge[] {
        return this.edges.filter(e => e.from === n || e.to === n);
    }

    private getOutNodes(n: VINode): VINode[] {
        return n.out.map(e => (e.from === n ? e.to : e.from));
    }

    private findNeighbours(n: VINode): VINode[] {
        return this.findEdges(n).map(e => (e.from === n ? e.to : e.from));
    }

    private findEdge(a: VINode, b: VINode): VIEdge {
        const edge = this.edges.find(e => (e.from === a && e.to === b) || (e.to === a && e.from === b));
        if (!edge)
            throw new Error("Could not find edge between " + a.id + " and " + b.id);

        return edge;
    }

    /** dijkstra on graph; returns list of edges */
    private shortestPath(src: VINode, dst: VINode, undirected?: boolean): VIEdge[] {
        const distArr = new Float64Array(this.nodes.length).fill(Infinity);
        const prev = new Array<VIEdge | null>(this.nodes.length).fill(null);
        distArr[src.id] = 0;

        const q = new Set<VINode>(this.nodes);
        while (q.size) {
            let u: VINode | null = null;
            let best = Infinity;
            for (const n of q) {
                if (distArr[n.id] < best) {
                    best = distArr[n.id];
                    u = n;
                }
            }
            if (!u)
                break;

            q.delete(u);
            if (u === dst)
                break;

            const neighbours = (undirected ? this.findNeighbours(u) : this.getOutNodes(u));
            for (const v of neighbours) {
                const alt = distArr[u.id] + dist(u, v);
                if (alt < distArr[v.id]) {
                    distArr[v.id] = alt;
                    prev[v.id] = this.findEdge(u, v);
                }
            }
        }

        const pathEdges: VIEdge[] = [];
        for (let n = dst; n !== src;) {
            const pe = prev[n.id];
            if (!pe) break;
            pathEdges.push(pe);
            n = pe.from === n ? pe.to : pe.from;
        }
        pathEdges.reverse();
        return pathEdges;
    }
}

/* ----------  private helper fns  ---------- */

const dist = (a: VINode, b: VINode) => Math.hypot(a.x - b.x, a.y - b.y);
const mean = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / arr.length;

/**
 * Check whether the line segment from a to b passes through
 * the circle of given radius around any other node.
 */
function intersectsAnyNodes(a: VINode, b: VINode, nodes: VINode[], radius: number): boolean {
    for (const n of nodes) {
        if (n === a || n === b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const l2 = dx * dx + dy * dy;
        if (l2 === 0) continue;
        // project n onto segment ab
        let t = ((n.x - a.x) * dx + (n.y - a.y) * dy) / l2;
        t = Math.max(0, Math.min(1, t));
        const projX = a.x + t * dx;
        const projY = a.y + t * dy;
        const d = Math.hypot(n.x - projX, n.y - projY);
        if (d <= radius) {
            return true;
        }
    }
    return false;
}

function intersectsAnyEdges(a: VINode, b: VINode, edges: VIEdge[]): boolean {
    return edges.some(e => segIntersect(a, b, e.from, e.to));
}

function segIntersect(p1: VINode, p2: VINode, p3: VINode, p4: VINode): boolean {
    const d = (p: VINode, q: VINode, r: VINode) =>
        (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);

    return (
        d(p1, p2, p3) * d(p1, p2, p4) < 0
        && d(p3, p4, p1) * d(p3, p4, p2) < 0
    );
}

function isEdgeDirected(e: VIEdge): boolean {
    // we consider directed when it appears only once in the endpoints' out lists
    return !(e.from.out.includes(e) && e.to.out.includes(e));
}

function reachable(src: VINode, dst: VINode): boolean {
    const stack = [src];
    const seen = new Set<VINode>();
    while (stack.length) {
        const n = stack.pop();
        if (!n)
            return false;
        if (n === dst)
            return true;

        seen.add(n);
        for (const e of n.out) {
            if (!seen.has(e.to)) {
                stack.push(e.to);
            }
        }
    }
    return false;
}

function removeFromEdgeSet(edges: VIEdge[], edge: VIEdge) {
    const index = edges.indexOf(edge);
    if (index >= 0) {
        edges.splice(index, 1);
    }
}

function addToEdgeSet(edges: VIEdge[], edge: VIEdge) {
    if (!edges.includes(edge)) {
        edges.push(edge);
    }
}

function generateNodePosition(
    centerX: number,
    centerY: number,
    nodeRadius: number,
    index: number,
): [number, number] {
    const goldenAngle = Math.PI / 3;
    const angle = index * goldenAngle;
    const radius = 3 * nodeRadius * Math.floor(angle / (2 * Math.PI));
    const x = centerX + radius * Math.cos(angle) + rand(-radius, radius);
    const y = centerY + radius * Math.sin(angle) + rand(-radius, radius);
    return [x, y];
}
